import React from 'react';
import { Input, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { ExportAssetType } from '../../util/useAddPermissions';
import Button from '../../../common/Button';

const MAX_BULK_ASSETS = 500;

type Props = {
  selectedAssets: any[];
  onSelectionChange: (assets: any[]) => void;
  onNext: () => void;
  type: ExportAssetType;
  subtypeKey: string;
  subtypeLabels: Partial<Record<string, string>>;
  /** When asset type has only one subtype, use this for display when API omits the field. */
  singleSubtypeFallback?: string;
  /** Asset rows (from parent query). */
  dataSource?: any[];
  loading?: boolean;
  /** Pagination config from useApiFilters (current, pageSize, total, showTotal, onChange, etc.). */
  pagination?: any;
  /** Called when user changes page or page size (from useApiFilters.onChangeAssetTable). */
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
};

const AssetSelectionTab: React.FC<Props> = ({
  selectedAssets,
  onSelectionChange,
  onNext,
  type: _type,
  subtypeKey,
  subtypeLabels,
  singleSubtypeFallback,
  dataSource,
  loading,
  pagination,
  onTableChange,
}) => {
  const selectedAssetIds = selectedAssets.map(a => a.id);
  const { t } = useTranslation(['asset', 'common']);

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: t('Type'),
      dataIndex: subtypeKey,
      key: 'subtype',
      render: (value: string) =>
        subtypeLabels[value] ??
        (singleSubtypeFallback ? subtypeLabels[singleSubtypeFallback] : undefined) ??
        value ??
        '-',
    },
    {
      title: t('Role'),
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: t('Custodian'),
      dataIndex: ['custodian', 'name'],
      key: 'custodian',
    },
    {
      title: t('Location'),
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: t('Criticality'),
      dataIndex: 'priority',
      key: 'priority',
      render: (value: string) =>
        value ? t(value.charAt(0).toUpperCase() + value.slice(1)) : '-',
    },
  ];

  const limitReached = selectedAssets.length > MAX_BULK_ASSETS;

  return (
    <div className="pb-4 pl-4 pr-4">
      <Table
        rowKey="id"
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        size="small"
        rowSelection={{
          selectedRowKeys: selectedAssetIds,
          onChange: (_keys, rows) => {
            const existingById = new Map(
              selectedAssets.map(a => [a.id, a])
            );
            rows.forEach(r => existingById.set(r.id, r));
            const deselectedIds = new Set(
              selectedAssetIds.filter(
                id => !_keys.includes(id)
              )
            );
            onSelectionChange(
              [...existingById.values()].filter(
                a => !deselectedIds.has(a.id)
              )
            );
          },
          preserveSelectedRowKeys: true,
        }}
        pagination={pagination}
        onChange={onTableChange}
      />

      <div className="flex justify-between items-center mt-6 pt-4">
        <span>
          {t('{{count}} asset(s) selected', {
            count: selectedAssets.length,
          })}
          {limitReached && (
            <span className="text-red-500 ml-2">
              {t('Maximum {{max}} assets allowed per operation.', {
                max: MAX_BULK_ASSETS,
              })}
            </span>
          )}
        </span>
        <Button
          variant="primary"
          disabled={selectedAssets.length === 0 || limitReached}
          onClick={onNext}
        >
          {t('Next')}
        </Button>
      </div>
    </div>
  );
};

export default AssetSelectionTab;
